import { NotFoundError } from "../../types";
import type { AuditEvent, OrganisationPolicies } from "../admin-types";
import { PermissionError, SeatLimitError, ValidationError } from "../errors";
import { checkSeats } from "../logic/invitations";
import { memberChangeRefusal, type MemberChange } from "../logic/members";
import type { IdentityApi, OrgRole } from "../types";
import { policiesOf, recordAudit } from "./audit";
import { requirePermission, type Ctx } from "./context";
import { createOrgApi } from "./organisation";
import { orgUsage } from "./views";

/**
 * The admin console in the mock (27 §6): member roles, suspension and removal, organisation policies and the audit
 * log, with the identity service's rules (`logic/members.ts`). Live: `crates/services/identity/src/routes/admin.rs`.
 */

type AdminApi = Pick<
  IdentityApi,
  | "changeMemberRole"
  | "suspendMember"
  | "reactivateMember"
  | "removeMember"
  | "getPolicies"
  | "updatePolicies"
  | "listAuditEvents"
>;

/** The most hours an organisation's session limit may be: Orthos's own 30 days. */
const SESSION_HOURS_MAX = 720;
const AUDIT_PAGE_SIZE = 50;

function refuse(kind: "permission" | "conflict" | "validation", message: string): never {
  if (kind === "permission") throw new PermissionError(message);
  throw new ValidationError(kind === "conflict" ? "userId" : "role", message);
}

export function createAdminApi(ctx: Ctx): AdminApi {
  const members = createOrgApi(ctx);

  const change = async (userId: string, wanted: MemberChange) => {
    await ctx.wait();
    const context = requirePermission(ctx, "org.manage_people");
    const { org, user } = context;
    const inOrg = ctx.state.memberships.filter((m) => m.organisationId === org.id);
    const target = inOrg.find((m) => m.userId === userId);
    const person = ctx.state.users.find((u) => u.id === userId);
    if (target === undefined || person === undefined) throw new NotFoundError("That member");
    const owners = inOrg.filter((m) => m.role === "owner" && m.suspended !== true).length;
    const refusal = memberChangeRefusal(
      { id: user.id, role: context.role },
      { id: userId, role: target.role },
      wanted,
      owners,
    );
    if (refusal !== undefined) refuse(refusal.kind, refusal.message);
    const before: OrgRole = target.role;
    switch (wanted.kind) {
      case "role": {
        if (wanted.role === before) return members.listMembers();
        if ((before === "guest") !== (wanted.role === "guest")) {
          const usage = orgUsage(ctx, org.id);
          const without =
            before === "guest"
              ? { ...usage, guests: usage.guests - 1 }
              : { ...usage, seats: usage.seats - 1 };
          const seats = checkSeats(org.allowances, without, [wanted.role]);
          if (!seats.ok) throw new SeatLimitError(seats.message);
        }
        target.role = wanted.role;
        break;
      }
      case "suspend":
        if (target.suspended === true) refuse("conflict", "This member is already suspended.");
        target.suspended = true;
        endSessions(ctx, userId, org.id);
        break;
      case "reactivate":
        if (target.suspended !== true) refuse("conflict", "This member is not suspended.");
        target.suspended = false;
        break;
      case "remove":
        ctx.state.memberships = ctx.state.memberships.filter((m) => m !== target);
        ctx.state.projectGrants = ctx.state.projectGrants.filter(
          (grant) => !(grant.organisationId === org.id && grant.userId === userId),
        );
        endSessions(ctx, userId, org.id);
        break;
    }
    const action = {
      role: "member.role_changed",
      suspend: "member.suspended",
      reactivate: "member.reactivated",
      remove: "member.removed",
    }[wanted.kind];
    const detail = wanted.kind === "role" ? { before, after: wanted.role } : { role: before };
    recordAudit(
      ctx,
      context,
      "people",
      action,
      { type: "member", id: userId, name: person.name },
      detail,
    );
    ctx.commit();
    return members.listMembers();
  };

  return {
    changeMemberRole: (userId, role) => change(userId, { kind: "role", role }),
    suspendMember: (userId) => change(userId, { kind: "suspend" }),
    reactivateMember: (userId) => change(userId, { kind: "reactivate" }),
    removeMember: (userId) => change(userId, { kind: "remove" }),

    async getPolicies() {
      await ctx.wait();
      return policiesOf(requirePermission(ctx, "org.manage_settings").org);
    },

    async updatePolicies(patch) {
      await ctx.wait();
      const context = requirePermission(ctx, "org.manage_settings");
      const before = policiesOf(context.org);
      const hours = patch.sessionMaxHours;
      if (
        typeof hours === "number" &&
        !(Number.isInteger(hours) && hours >= 1 && hours <= SESSION_HOURS_MAX)
      )
        throw new ValidationError(
          "sessionMaxHours",
          `Choose a session length from 1 to ${String(SESSION_HOURS_MAX)} hours.`,
        );
      const after: OrganisationPolicies = {
        memberProjectAccess: patch.memberProjectAccess ?? before.memberProjectAccess,
        projectCreation: patch.projectCreation ?? before.projectCreation,
        sessionMaxHours: hours === undefined ? before.sessionMaxHours : (hours ?? undefined),
      };
      context.org.policies = after;
      const { org } = context;
      recordAudit(
        ctx,
        context,
        "policies",
        "policy.changed",
        { type: "organisation", id: org.id, name: org.name },
        {
          before,
          after,
        },
      );
      ctx.commit();
      return policiesOf(org);
    },

    async listAuditEvents(query) {
      await ctx.wait();
      const { org } = requirePermission(ctx, "org.manage_settings");
      const text = query.q?.trim().toLowerCase() ?? "";
      const from = query.from === undefined ? undefined : Date.parse(query.from);
      const to = query.to === undefined ? undefined : Date.parse(query.to) + DAY_MS;
      const all = [...(ctx.state.audit ?? [])]
        .filter((event) => event.organisationId === org.id)
        .filter((event) => query.category === undefined || event.category === query.category)
        .filter((event) => query.actor === undefined || event.actorId === query.actor)
        .filter((event) => from === undefined || event.occurredAt >= from)
        .filter((event) => to === undefined || event.occurredAt < to)
        .filter(
          (event) =>
            text === "" ||
            [event.actorName, event.targetName, event.action].some((field) =>
              field.toLowerCase().includes(text),
            ),
        )
        .reverse();
      const start =
        query.before === undefined ? 0 : all.findIndex((e) => e.id === query.before) + 1;
      const limit = query.limit ?? AUDIT_PAGE_SIZE;
      const page = all.slice(start, start + limit);
      const events: AuditEvent[] = page.map((event) => ({
        id: event.id,
        occurredAt: new Date(event.occurredAt).toISOString(),
        actor: { type: "person", id: event.actorId, name: event.actorName },
        category: event.category,
        action: event.action,
        target: { type: event.targetType, id: event.targetId, name: event.targetName },
        result: "success",
        ipAddress: undefined,
        requestId: event.id,
        detail: event.detail,
      }));
      const more = start + limit < all.length;
      return { events, next: more ? page.at(-1)?.id : undefined };
    },
  };
}

const DAY_MS = 86_400_000;

/** Ends the person's sessions in the organisation, as the service does on suspension and removal. */
function endSessions(ctx: Ctx, userId: string, organisationId: string): void {
  for (const session of ctx.state.sessions) {
    if (
      session.userId === userId &&
      session.organisationId === organisationId &&
      session.endedAt === undefined
    ) {
      session.endedAt = ctx.now();
      session.endReason = "revoked";
    }
  }
}
